/**
 * The Teams tab while teams are being built (draft → pre_registration → lobby).
 *
 * The teams themselves are the point of this screen, so they get the space. The
 * two things that used to sit permanently above and below them now stay out of
 * the way until asked for:
 *
 * - the name pool moved into a header dropdown ({@link TeamNameMenu});
 * - the name select and colour palette appear only while actually adding a team,
 *   rather than occupying a full panel whether or not one is being created.
 */

import { useState } from 'react';
import type { TrlDisruptionRecord, TrlTeam } from '@swtorbit/shared-socket-events';
import { TeamCard, type TrlSabotagePlacementSummary } from './TeamCard';
import { TeamColorGrid } from './TeamColorGrid';
import { TeamNameMenu } from './TeamNameMenu';

interface TrlTeamSetupViewProps {
  teams: TrlTeam[];
  availableNames: string[];
  usedNames: Set<string>;
  disabledCoreNames: string[];
  customNames: string[];
  createColor: string | undefined;
  onPickCreateColor: (color: string) => void;
  playerNameById: (playerId: string) => string;
  unassignedPlayers: { playerId: string; name: string }[];
  startLabelByTeam: Map<string, string | null>;
  inLobby: boolean;
  minMembers: number | null;
  activeBlocks: Map<string, TrlDisruptionRecord>;
  sabotageEnabled: boolean;
  sabotagePlacementByTeam: Map<string, TrlSabotagePlacementSummary>;
  onCreate: (name: string) => void;
  onRename: (teamId: string, name: string) => void;
  onRecolor: (teamId: string, color: string) => void;
  onDelete: (teamId: string) => void;
  onAssign: (teamId: string, playerId: string) => void;
  onRemove: (teamId: string, playerId: string) => void;
  onSetCaptain: (teamId: string, playerId: string) => void;
  onClearBlock: (playerId: string) => void;
  onSaveNames: (disabledCoreNames: string[], customNames: string[]) => void;
}

export function TrlTeamSetupView({
  teams,
  availableNames,
  usedNames,
  disabledCoreNames,
  customNames,
  createColor,
  onPickCreateColor,
  playerNameById,
  unassignedPlayers,
  startLabelByTeam,
  inLobby,
  minMembers,
  activeBlocks,
  sabotageEnabled,
  sabotagePlacementByTeam,
  onCreate,
  onRename,
  onRecolor,
  onDelete,
  onAssign,
  onRemove,
  onSetCaptain,
  onClearBlock,
  onSaveNames,
}: TrlTeamSetupViewProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [createName, setCreateName] = useState('');

  const noNamesLeft = availableNames.length === 0;

  const closeAdd = () => {
    setIsAdding(false);
    setCreateName('');
  };

  const handleCreate = () => {
    if (!createName) return;
    onCreate(createName);
    closeAdd();
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
        <h2 className="m-0">
          👥 Teams <span className="badge badge--sm">{teams.length}</span>
        </h2>
        <div className="d-flex align-items-center gap-xs">
          <TeamNameMenu
            disabledCoreNames={disabledCoreNames}
            customNames={customNames}
            usedNames={usedNames}
            onSave={onSaveNames}
          />
          <button
            className="btn btn--green btn--sm"
            disabled={isAdding || noNamesLeft}
            title={noNamesLeft ? 'No names left in the pool — add one under Team names' : 'Add a team'}
            onClick={() => setIsAdding(true)}
          >
            ➕ Add Team
          </button>
        </div>
      </div>

      {isAdding && (
        <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs">
          <div className="d-flex gap-xs align-items-center flex-wrap">
            <select
              className="select"
              value={createName}
              autoFocus
              onChange={(e) => setCreateName(e.target.value)}
              style={{ flex: 1, minWidth: '160px' }}
            >
              <option value="">-- Choose a team name --</option>
              {availableNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <button className="btn btn--green" onClick={handleCreate} disabled={!createName || !createColor}>
              Create
            </button>
            <button className="btn btn--outline btn--slate" onClick={closeAdd}>
              Cancel
            </button>
          </div>
          <span className="field__hint fs-xs">
            Team colour — also used for the team&apos;s Discord role. Greyed-out colours are taken.
          </span>
          <TeamColorGrid teams={teams} selected={createColor} onSelect={onPickCreateColor} />
        </div>
      )}

      {teams.length === 0 ? (
        <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-lg text-center d-flex flex-column gap-xs">
          <span className="fs-lg">🧭</span>
          <span className="text-cyan">No teams yet.</span>
          <span className="field__hint fs-xs">
            Use <strong>➕ Add Team</strong> above to create the first one.
          </span>
        </div>
      ) : (
        <div className="trl-teams__grid">
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              teams={teams}
              availableNames={availableNames}
              playerNameById={playerNameById}
              unassignedPlayers={unassignedPlayers}
              startStatus={{ inLobby, planetLabel: startLabelByTeam.get(team.id) ?? null }}
              minMembers={minMembers}
              activeBlocks={activeBlocks}
              sabotageStatus={{
                show: sabotageEnabled || sabotagePlacementByTeam.has(team.id),
                placement: sabotagePlacementByTeam.get(team.id) ?? null,
                inLobby,
              }}
              onRename={onRename}
              onRecolor={onRecolor}
              onDelete={onDelete}
              onAssign={onAssign}
              onRemove={onRemove}
              onSetCaptain={onSetCaptain}
              onClearBlock={onClearBlock}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default TrlTeamSetupView;
